import Breadcrumb from '@/components/feature/Breadcrumb';
import JsonLd from '@/components/feature/JsonLd';
import { siteInfo } from '@/mocks/comics';
import { useState } from 'react';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9peoh1n2trhb35gve40', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        if (typeof msg === 'string' && msg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(typeof msg === 'string' ? msg : '提交失败，请稍后重试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于韩漫画网',
    description: siteInfo.description,
    url: 'https://www.hanmanhua.com/about',
  };

  return (
    <>
      <JsonLd data={aboutJsonLd} />
      <main className="pt-20 pb-12">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          <section className="mb-16">
            <div className="max-w-[800px]">
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-4">
                关于韩漫画网
              </h1>
              <p className="text-sm text-foreground-600 leading-relaxed">
                韩漫画网创立于{siteInfo.established}，是国内领先的韩国漫画免费阅读平台。
                我们致力于为广大漫画爱好者提供下拉式完整版韩漫免费阅读和无删减漫画免费阅读服务，
                打造最全面的韩漫大全阅读社区。
              </p>
              <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
                {siteInfo.description}
              </p>
            </div>
          </section>

          <section className="mb-16 bg-primary-500 rounded-2xl p-8 md:p-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-3xl md:text-4xl font-bold font-heading text-background-50">
                  {siteInfo.stats.totalComics}
                </div>
                <div className="mt-1 text-sm text-background-50/80">韩漫大全</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-bold font-heading text-background-50">
                  {siteInfo.stats.dailyUpdate}
                </div>
                <div className="mt-1 text-sm text-background-50/80">每日更新</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-bold font-heading text-background-50">
                  {siteInfo.stats.registeredUsers}
                </div>
                <div className="mt-1 text-sm text-background-50/80">注册用户</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-bold font-heading text-background-50">
                  {siteInfo.stats.dailyActiveUsers}
                </div>
                <div className="mt-1 text-sm text-background-50/80">日活用户</div>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-8 text-center">
              我们的优势
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {siteInfo.features.map((feature) => (
                <div
                  key={feature.title}
                  className="p-6 bg-background-50 border border-background-200/70 rounded-lg"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 mb-4">
                    <i className={`${feature.icon} text-xl text-accent-600`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-8 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { name: '小雨', text: '韩漫画网的漫画资源真的太丰富了！我在上面看完了好几部之前找不到的韩漫，下拉式阅读体验也很棒，推荐给所有韩漫爱好者！', rating: 5 },
                { name: '阿杰', text: '每天都有更新，追更党的天堂。无删减版本太重要了，能完整欣赏作者的创作。界面简洁干净，阅读体验极佳。', rating: 5 },
                { name: '星辰', text: '从2019年就开始用韩漫画网了，看着它一步步发展壮大。分类很清晰，找漫画很方便，希望越做越好！', rating: 5 },
              ].map((review, index) => (
                <div
                  key={index}
                  className="p-6 bg-background-50 border border-background-200/70 rounded-lg"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    "{review.text}"
                  </p>
                  <p className="text-sm font-medium text-foreground-900">—— {review.name}</p>
                </div>
              ))}
            </div>
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <section>
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-6">
                联系我们
              </h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 shrink-0">
                    <i className="ri-mail-line text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">电子邮箱</p>
                    <p className="text-sm text-foreground-900">{siteInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 shrink-0">
                    <i className="ri-phone-line text-primary-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">客服电话</p>
                    <p className="text-sm text-foreground-900">{siteInfo.phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 shrink-0">
                    <i className="ri-map-pin-line text-secondary-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">公司地址</p>
                    <p className="text-sm text-foreground-900">{siteInfo.address}</p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-6">
                给我们留言
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />
                <div>
                  <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    您的称呼
                  </label>
                  <input
                    id="about-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-colors"
                    placeholder="请输入您的称呼"
                  />
                </div>
                <div>
                  <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    电子邮箱
                  </label>
                  <input
                    id="about-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="about-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-colors resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full py-3 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  发送留言
                </button>
                {formStatus === 'success' && (
                  <p className="text-sm text-green-600 text-center">留言发送成功，我们会尽快回复您！</p>
                )}
                {formStatus === 'error' && (
                  <p className="text-sm text-red-500 text-center">{formError}</p>
                )}
              </form>
            </section>
          </div>
        </div>
      </main>
    </>
  );
}